import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography, Stack, Grow } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import {
  MediaRecorderState,
  ProgressModal,
  FeedbackSnackbar,
  uploadToScreenApp,
  stopMediaRecording,
} from './common';

export const AudioRecorder: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<MediaRecorderState>({
    isRecording: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState('00:00');

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const visualizerRef = useRef<HTMLCanvasElement>(null);
  const animationFrame = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const recordedBlob = useRef<Blob | null>(null);
  const audioStream = useRef<MediaStream | null>(null);

  const updateRecordingTime = () => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedTime / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (elapsedTime % 60).toString().padStart(2, '0');
    setRecordingTime(`${minutes}:${seconds}`);
  };

  const drawVisualizer = () => {
    if (!visualizerRef.current || !analyser.current) return;

    const canvasCtx = visualizerRef.current.getContext('2d');
    if (!canvasCtx) return;

    const { width, height } = visualizerRef.current;
    const dataArray = new Uint8Array(analyser.current.frequencyBinCount);

    const draw = () => {
      if (!analyser.current) return;

      analyser.current.getByteFrequencyData(dataArray);
      canvasCtx.clearRect(0, 0, width, height);

      const barWidth = Math.ceil(
        (width / analyser.current.frequencyBinCount) * 2.5
      );
      const centerY = height / 2;
      let x = 0;

      canvasCtx.fillStyle = '#d1d5db';
      for (let i = 0; i < analyser.current.frequencyBinCount; i += 1) {
        const barHeight = Math.min(dataArray[i] * 1.5, height);
        canvasCtx.fillRect(
          Math.floor(x),
          Math.floor(centerY - barHeight / 2),
          Math.floor(barWidth),
          Math.floor(barHeight)
        );
        x += barWidth + 1;
      }

      animationFrame.current = requestAnimationFrame(draw);
    };

    draw();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      audioContext.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      analyser.current = audioContext.current.createAnalyser();
      const source = audioContext.current.createMediaStreamSource(stream);
      source.connect(analyser.current);

      analyser.current.fftSize = 2048;
      analyser.current.minDecibels = -90;
      analyser.current.maxDecibels = -10;
      analyser.current.smoothingTimeConstant = 0.85;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      // Try to create with preferred codec, fallback to browser default if not supported
      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        mediaRecorder.current = new MediaRecorder(stream, options);
      } else {
        mediaRecorder.current = new MediaRecorder(stream);
      }

      // Set up event handlers before starting
      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(chunks.current, {
          type: mediaRecorder.current?.mimeType || 'audio/webm;codecs=opus',
        });

        recordedBlob.current = audioBlob;

        uploadToScreenApp(
          audioBlob,
          'recording.webm',
          () => setState((prev) => ({ ...prev, uploading: true })),
          (progress) => setState((prev) => ({ ...prev, progress })),
          () => setState((prev) => ({ ...prev, uploadComplete: true })),
          (error) => setState((prev) => ({ ...prev, error }))
        );

        // Clean up
        setState((prev) => ({ ...prev, isRecording: false }));
        if (timerInterval.current) clearInterval(timerInterval.current);
        if (visualizerRef.current) visualizerRef.current.style.display = 'none';
        chunks.current = []; // Clear chunks for next recording
      };

      // Start recording with a timeslice to get data periodically
      mediaRecorder.current.start(1000);
      setState((prev) => ({ ...prev, isRecording: true }));
      startTimeRef.current = Date.now();
      updateRecordingTime();
      timerInterval.current = setInterval(updateRecordingTime, 1000);

      if (visualizerRef.current) {
        visualizerRef.current.style.display = 'block';
        drawVisualizer();
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Error accessing microphone. Please check permissions.',
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      // Stop recording and cleanup
      stopMediaRecording(
        mediaRecorder,
        [mediaRecorder.current.stream],
        animationFrame
      );
    }
  };

  const togglePause = () => {
    if (!mediaRecorder.current) return;

    if (isPaused) {
      mediaRecorder.current.resume();
      startTimeRef.current = Date.now() - pausedTimeRef.current;
      timerInterval.current = setInterval(updateRecordingTime, 1000);
      drawVisualizer();
    } else {
      mediaRecorder.current.pause();
      pausedTimeRef.current = Date.now() - startTimeRef.current;
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    }
    setIsPaused(!isPaused);
  };

  const handleReset = () => {
    // Reset all state to initial values
    setState({
      isRecording: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
    setIsPaused(false);
    setRecordingTime('00:00');

    // Clear all refs
    recordedBlob.current = null;
    chunks.current = [];
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;

    // Stop media recording and clear streams
    stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    audioStream.current = null;

    // Clear intervals and animation frames
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    // Close audio context
    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }
    audioContext.current = null;
    analyser.current = null;

    // Clear visualizer
    if (visualizerRef.current) {
      const ctx = visualizerRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          visualizerRef.current.width,
          visualizerRef.current.height
        );
      }
      visualizerRef.current.style.display = 'none';
    }
  };

  const handleSaveAgain = () => {
    if (recordedBlob.current) {
      uploadToScreenApp(
        recordedBlob.current,
        'audio-recording.webm',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) => setState((prev) => ({ ...prev, error }))
      );
    }
  };

  useEffect(() => {
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  return (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      {state.error?.includes('microphone') && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontStyle: 'italic' }}
        >
          {t('audioRecorder.messages.microphoneAccess')}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mb: 3, minHeight: 56 }}
      >
        {!state.isRecording && !state.uploading && (
          <Button
            variant="contained"
            color="error"
            onClick={startRecording}
            startIcon={<MicIcon />}
            size="large"
            sx={{
              minWidth: 140,
              px: 3,
              borderRadius: 28,
              py: 2,
              fontSize: '1.2rem',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: '#d32f2f',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            {t('audioRecorder.buttons.startRecording')}
          </Button>
        )}
        {state.isRecording && (
          <>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="contained"
                color="error"
                onClick={stopRecording}
                startIcon={<StopIcon />}
                size="large"
                sx={{
                  minWidth: 140,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.2rem',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                {recordingTime}
              </Button>
            </Grow>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={togglePause}
                startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                size="large"
                sx={{
                  minWidth: 140,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.2rem',
                  '& .MuiSvgIcon-root': {
                    color: 'secondary.main',
                  },
                  '&:hover': {
                    '& .MuiSvgIcon-root': {
                      color: 'secondary.main',
                    },
                  },
                }}
              >
                {isPaused
                  ? t('audioRecorder.buttons.resume')
                  : t('audioRecorder.buttons.pause')}
              </Button>
            </Grow>
          </>
        )}
      </Stack>

      <Box
        component="canvas"
        ref={visualizerRef}
        sx={{
          width: '100%',
          height: 150,
          my: 2.5,
          display: 'none',
        }}
      />

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('audioRecorder.messages.processing')}
        completeMessage={t('audioRecorder.messages.complete')}
        onReset={handleReset}
        onSaveAgain={handleSaveAgain}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || ''}
        severity="error"
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default AudioRecorder;
